import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DocumentThumbnail from '@/components/feature/DocumentThumbnail';
import OcrResultPanel from '@/components/feature/OcrResultPanel';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

interface DocumentDetail {
  id: number;
  company_id: number;
  kyc_request_id: number | null;
  customer_id: number;
  uploaded_by: number;
  doc_type: string;
  doc_category: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  checksum: string | null;
  verification_status: string;
  reviewed_by: number | null;
  review_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  customer_name: string;
  uploaded_by_name: string;
  reviewed_by_name: string | null;
  ocr_data?: {
    rawText: string;
    fields: Record<string, string | null>;
    confidence: number;
    processedAt: string;
    engine: string;
  } | null;
  verification_result?: {
    compositeScore: number;
    riskLevel: string;
    flags: { type: string; label: string; severity: string }[];
    processedAt: string;
  } | null;
}

interface DocumentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: number | null;
  onReviewSuccess?: () => void;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
};

export default function DocumentDetailModal({ isOpen, onClose, documentId, onReviewSuccess }: DocumentDetailModalProps) {
  const { accessToken, user } = useAuth();
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Review state
  const [reviewAction, setReviewAction] = useState<'verified' | 'failed' | null>(null);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const canReview = user?.role === 'superadmin' || user?.role === 'company_admin' || user?.role === 'kyc_officer';

  const fetchDoc = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.message || 'Failed to load document');
        setDoc(null);
      } else {
        setDoc(json.data);
      }
    } catch {
      setError('Network error loading document');
    } finally {
      setLoading(false);
    }
  }, [documentId, accessToken]);

  useEffect(() => {
    if (isOpen && documentId) {
      fetchDoc();
      setReviewAction(null);
      setReviewReason('');
      setReviewError('');
    }
  }, [isOpen, documentId, fetchDoc]);

  const handleReviewSubmit = useCallback(async () => {
    if (!reviewAction || !documentId) return;
    if (reviewAction === 'failed' && !reviewReason.trim()) {
      setReviewError('Reason is required when disapproving');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    try {
      const res = await fetch(`${API_BASE}/documents/${documentId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: reviewAction, reason: reviewAction === 'failed' ? reviewReason.trim() : null }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setReviewError(json.message || 'Review failed');
        setReviewSubmitting(false);
        return;
      }
      await fetchDoc();
      setReviewAction(null);
      setReviewReason('');
      onReviewSuccess?.();
    } catch {
      setReviewError('Network error during review');
    } finally {
      setReviewSubmitting(false);
    }
  }, [reviewAction, reviewReason, documentId, accessToken, fetchDoc, onReviewSuccess]);

  if (!isOpen || !documentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h2 className="text-lg font-bold text-stone-900">Document Detail</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>

        {loading && (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
          </div>
        )}

        {error && (
          <div className="p-6">
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
              {error}
            </div>
          </div>
        )}

        {doc && !loading && (
          <div className="p-6 space-y-6">
            {/* Top summary */}
            <div className="flex items-start gap-4">
              <DocumentThumbnail
                documentId={doc.id}
                mimeType={doc.mime_type}
                fileName={doc.file_name}
                size="lg"
                rounded="xl"
                onClick={() => {}}
              />
              <div className="flex-1 min-w-0">
                <p className="text-stone-900 font-semibold text-base truncate">{doc.file_name}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[doc.verification_status] || 'bg-stone-100 text-stone-700'}`}>
                    {doc.verification_status}
                  </span>
                  <span className="text-stone-400 text-xs">{doc.doc_type}</span>
                  <span className="text-stone-300 text-xs">·</span>
                  <span className="text-stone-400 text-xs">{doc.doc_category}</span>
                  <span className="text-stone-300 text-xs">·</span>
                  <span className="text-stone-400 text-xs">{formatSize(doc.file_size)}</span>
                </div>
                {/* OCR status badge */}
                {doc.ocr_data && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700">
                      <i className="ri-scan-line mr-0.5" />
                      OCR {doc.ocr_data.confidence}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`${API_BASE}/documents/${doc.id}/download?inline=true`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line" />
                View File
              </a>
              <a
                href={`${API_BASE}/documents/${doc.id}/download`}
                className="bg-white border border-stone-200 hover:bg-stone-50 text-stone-700 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-line" />
                Download
              </a>
              {canReview && doc.verification_status === 'pending' && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={() => { setReviewAction('verified'); setReviewError(''); }}
                    className={`px-3 py-2 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                      reviewAction === 'verified'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    <i className="ri-check-line mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => { setReviewAction('failed'); setReviewError(''); }}
                    className={`px-3 py-2 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                      reviewAction === 'failed'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    <i className="ri-close-line mr-1" />
                    Disapprove
                  </button>
                </div>
              )}
            </div>

            {/* OCR + Tamper Panel */}
            <OcrResultPanel
              documentId={doc.id}
              ocrData={doc.ocr_data || null}
              verificationResult={doc.verification_result || null}
              onOcrComplete={fetchDoc}
            />

            {/* Review form */}
            {reviewAction && (
              <div className="bg-stone-50 rounded-xl border border-stone-100 p-4">
                <h4 className="text-sm font-semibold text-stone-900 mb-2">
                  {reviewAction === 'verified' ? 'Approve Document' : 'Disapprove Document'}
                </h4>
                {reviewAction === 'failed' && (
                  <div className="mb-3">
                    <label className="block text-stone-700 text-xs font-medium mb-1.5">Reason for Disapproval *</label>
                    <textarea
                      value={reviewReason}
                      onChange={(e) => setReviewReason(e.target.value)}
                      placeholder="Enter reason..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none bg-white"
                    />
                    <p className="text-stone-300 text-[10px] text-right mt-0.5">{reviewReason.length}/500</p>
                  </div>
                )}
                {reviewError && (
                  <div className="mb-3 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
                    {reviewError}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => { setReviewAction(null); setReviewReason(''); setReviewError(''); }}
                    className="px-3 py-2 rounded-full text-xs font-medium text-stone-600 hover:bg-stone-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReviewSubmit}
                    disabled={reviewSubmitting}
                    className="bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5"
                  >
                    {reviewSubmitting && <i className="ri-loader-4-line animate-spin" />}
                    Submit
                  </button>
                </div>
              </div>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Document ID</p>
                  <p className="text-stone-900 text-sm font-mono">{doc.id}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">File Name</p>
                  <p className="text-stone-900 text-sm">{doc.file_name}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">MIME Type</p>
                  <p className="text-stone-900 text-sm font-mono">{doc.mime_type}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">File Size</p>
                  <p className="text-stone-900 text-sm">{formatSize(doc.file_size)}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Checksum</p>
                  <p className="text-stone-900 text-sm font-mono truncate">{doc.checksum || 'N/A'}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Document Type</p>
                  <p className="text-stone-900 text-sm">{doc.doc_type}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Category</p>
                  <p className="text-stone-900 text-sm capitalize">{doc.doc_category}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Customer</p>
                  <p className="text-stone-900 text-sm">{doc.customer_name}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Uploaded By</p>
                  <p className="text-stone-900 text-sm">{doc.uploaded_by_name}</p>
                </div>
                {doc.reviewed_by_name && (
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Reviewed By</p>
                    <p className="text-stone-900 text-sm">{doc.reviewed_by_name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="bg-stone-50 rounded-xl border border-stone-100 p-4">
              <h4 className="text-sm font-semibold text-stone-900 mb-3">Timeline</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Created</span>
                  <span className="text-stone-900 text-xs">{new Date(doc.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Updated</span>
                  <span className="text-stone-900 text-xs">{new Date(doc.updated_at).toLocaleString()}</span>
                </div>
                {doc.reviewed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-stone-500 text-xs">Reviewed</span>
                    <span className="text-stone-900 text-xs">{new Date(doc.reviewed_at).toLocaleString()}</span>
                  </div>
                )}
                {doc.review_reason && (
                  <div className="mt-2 pt-2 border-t border-stone-100">
                    <p className="text-stone-500 text-xs mb-1">Review Reason</p>
                    <p className="text-stone-700 text-xs">{doc.review_reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
